import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { computeResults } from './mocks';
import type { Hypotheses, PlanBootstrap, Scenario } from './types';

export function usePlanBootstrap(projectId: string) {
  return useQuery<PlanBootstrap>({
    queryKey: ['plan2cost', 'project', projectId],
    queryFn: async () => {
      const res = await fetch(`/api/plan/projects/${projectId}`);
      if (!res.ok) throw new Error('Impossible de charger le projet');
      return res.json();
    },
    staleTime: Infinity,
  });
}

function now() {
  const d = new Date();
  return `${d.toLocaleDateString('fr-FR')} ${d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
}

/** Local scenario store: per-scenario hypotheses + simulated runs. */
export function useScenarios(initial: Scenario[] | undefined) {
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const timers = useRef<Record<string, ReturnType<typeof setInterval>>>({});

  useEffect(() => {
    if (initial && scenarios.length === 0) setScenarios(initial);
  }, [initial, scenarios.length]);

  useEffect(() => () => Object.values(timers.current).forEach(clearInterval), []);

  const updateHypotheses = useCallback((id: string, patch: Partial<Hypotheses>) => {
    setScenarios((list) => list.map((s) => (s.id === id
      ? { ...s, hypotheses: { ...s.hypotheses, ...patch }, status: s.status === 'done' ? 'stale' : s.status, updatedAt: now() }
      : s)));
  }, []);

  const run = useCallback((id: string) => {
    setScenarios((list) => list.map((s) => (s.id === id ? { ...s, status: 'running', progress: 0 } : s)));
    clearInterval(timers.current[id]);
    timers.current[id] = setInterval(() => {
      setScenarios((list) => list.map((s) => {
        if (s.id !== id) return s;
        const progress = Math.min(100, s.progress + 9);
        if (progress < 100) return { ...s, progress };
        clearInterval(timers.current[id]);
        return { ...s, progress: 100, status: 'done', results: computeResults(s.hypotheses), updatedAt: now() };
      }));
    }, 260);
  }, []);

  const duplicate = useCallback((id: string) => {
    const newId = `sc-${Date.now()}`;
    setScenarios((list) => {
      const src = list.find((s) => s.id === id);
      if (!src) return list;
      return [...list, { ...src, id: newId, name: `${src.name} (copie)`, hypotheses: { ...src.hypotheses, usages: [...src.hypotheses.usages] }, updatedAt: now() }];
    });
    return newId;
  }, []);

  const create = useCallback((h: Hypotheses) => {
    const id = `sc-${Date.now()}`;
    setScenarios((list) => [...list, { id, name: `Nouveau scénario ${list.length + 1}`, status: 'draft', progress: 0, hypotheses: { ...h, usages: [...h.usages] }, updatedAt: now() }]);
    return id;
  }, []);

  const rename = useCallback((id: string, name: string) => {
    setScenarios((list) => list.map((s) => (s.id === id ? { ...s, name } : s)));
  }, []);

  return { scenarios, updateHypotheses, run, duplicate, create, rename };
}
